<template>
  <div>
    <div class="is-flex-tablet">
      <div class="modal-card call-log">
        <div class="view-container box mb-5">
          <div class="columns is-multiline is-mobile box">
            <div class="column is-12">
              <dealer-selector v-model="selectedDealer" :required="true"></dealer-selector>
            </div>
            <div class="column is-12">
              <div class="field">
                <label class="label">
                  Search Criteria
                  <span v-if="selectedCustomer" class="selected-value-display is-pulled-right has-text-link">{{ selectedCustomer.fullName }} {{ customerExtraDisplayOption(selectedCustomer) }}</span>
                </label>
                <div class="control is-fullwidth">
                   <b-autocomplete
                    ref="searchInput"
                    tabindex="3"
                    :data="searchList"
                    name="searchCriteria"
                    field="searchCriteria"
                    @input="getAsyncMatchList"
                    :class="{ 'is-danger': errors.has('searchCriteria') }"
                    v-model="searchCriteria"
                    :expanded="true"
                    :clearable="true"
                    key="searchCriteria">
                    <template slot-scope="props">
                      <small :key="'mobile-key-' + props.option" @click="searchCriteria = props.option">
                        {{ props.option }}
                      </small>
                    </template>
                  </b-autocomplete>
                </div>
              </div>
            </div>
          </div>
          <div class="columns is-mobile">
            <div class="column is-12 box" v-if="selectedCustomer">
              <section-header
                title="Customer Details"
                :collapsed="customerDetailsCollapsed"
                v-on:collapsed-status-changed="onCustomerDetailsCollapsedChanged">
              </section-header>
              <div class="columns is-multiline">
                <div class="column is-6">
                  <div class="field">
                    <label class="label">
                      Name
                      <dealer-peak-control v-if="selectedCustomer.customerId" :key="selectedCustomer.id" :customerId="selectedCustomer.id"></dealer-peak-control>
                    </label>
                    <div class="field">
                      <a class="is-link" @click="openCustomerRecord()">
                        {{ selectedCustomer.fullName }}
                      </a>
                      <customer-pop-component v-model="selectedCustomer.id"></customer-pop-component>
                    </div>
                  </div>
                  <div class="field">
                    <label class="label">Email</label>
                    <div class="field">
                      <a class="clickable" v-clipboard:copy="selectedCustomer.email" @click.stop.prevent="onCopy('email', selectedCustomer.email)">{{ selectedCustomer.email }}</a>
                    </div>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label">Telephone</label>
                    <div class="field">
                      <click-to-call :destinationLineProp="selectedCustomer.telephone" :destinationNameProp="selectedCustomer.fullName"></click-to-call>
                    </div>
                  </div>
                  <div class="field">
                    <label class="label">Mobile</label>
                    <div class="field">
                      <click-to-call :destinationLineProp="selectedCustomer.mobile" :destinationNameProp="selectedCustomer.fullName"></click-to-call>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="columns is-multiline is-mobile">
            <div id="last-contacts" class="column is-12 box">
              <section-header title="Last Contacted" v-on:collapsed-status-changed="onShowLastContacted" :collapsed="!lastCustomerContactsOpen" parentSelector="#last-contacts"></section-header>
              <div class="card-header" role="button">
                <div class="card-header-title is-size-7 stacked-heading">
                  <span>Last 100 customer contacts</span>
                  <span v-if="selectedLastContact" class="selected-value-display has-text-link">{{ selectedLastContact.fullName }} {{ customerExtraDisplayOption(selectedLastContact) }}</span>
                </div>
              </div>
              <div class="card-content">
                <b-button :class="{ 'boption': true, 'button': true, 'is-info': selectedLastContact && selectedLastContact.id === customer.id}" v-for="customer in (matchedLastContacts.length ? matchedLastContacts : lastCustomerContacts)" :key="customer.id" @click="onLastContactSelected(customer)" v-tooltip="customer.fullName">
                  <b-icon v-if="customer.haveOpenLead" pack="fa" icon="badge-check" type="is-success"></b-icon>
                  <span>{{ customer.fullName }}</span>
                </b-button>
              </div>
            </div>
          </div>
          <div class="columns is-multiline is-mobile">
            <div id="search-results" class="column is-12 box">
              <section-header title="Results" v-on:collapsed-status-changed="onShowCustomerSearch" :collapsed="!customerSearchOpen" parentSelector="#search-results"></section-header>
              <div class="card-header" role="button">
                <p class="card-header-title is-size-7">
                  Customer search results
                  <span v-if="selectedCustomer" class="selected-value-display is-pulled-right has-text-link" style="padding-left: 10px;">{{ selectedCustomer.fullName }} {{ customerExtraDisplayOption(selectedCustomer) }}</span>
                </p>
              </div>
              <div class="card-content">
                <b-table
                  ref="search-result-table"
                  :data="customerSearchResults"
                  :selected.sync="selectedCustomer"
                  :height="'auto'"
                  :sticky-header="true"
                  :mobile-cards="true"
                  :narrowed="true"
                  :loading="isSearchingCustomer"
                  @select="onRowSelected"
                  @dblclick="onRowSelected"
                  :focusable="true">
                  <template slot-scope="props">
                    <b-table-column field="fullName" v-tooltip="props.row.fullName" label="Name">
                      {{ props.row.fullName | truncate(10) }}
                      <b-icon v-if="props.row.haveOpenLead" icon="badge-check" pack="fa" type="is-success"></b-icon>
                    </b-table-column>
                    <b-table-column field="hasRelationships" label="Related" width="60" centered>
                      <b-icon v-if="props.row.hasRelationships" pack="fa" icon="users" type="is-info" v-tooltip="'Has related customers'"></b-icon>
                    </b-table-column>
                    <b-table-column field="mobile" v-tooltip="props.row.mobile" label="Mobile">
                      {{ props.row.mobile | truncate(10) }}
                    </b-table-column>
                    <b-table-column field="email" v-tooltip="props.row.email" label="Email">
                      {{ props.row.email | truncate(10) }}
                    </b-table-column>
                    <b-table-column field="leadStatus" v-tooltip="props.row.leadStatus" label="Lead Status">
                      {{ props.row.leadStatus | truncate(10) }}
                    </b-table-column>
                  </template>
                  <template slot="empty">
                    <section class="section">
                      <div class="has-text-centered">
                        <p><b-icon icon="emoticon-sad" size="is-large"></b-icon></p>
                        <p>No customers found</p>
                      </div>
                    </section>
                  </template>
                </b-table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import axios from 'axios'
import utilitiesMixin from '@/mixins/generic/utilities'
import customerService from '@/services/customerService'
import DealerSelector from '@/components/generic/DealerSelector'
import ClickToCallComponent from '@/components/generic/ClickToCallComponent'
import SectionHeader from '@/components/generic/SectionHeader'
import CustomerPopComponent from '@/components/generic/CustomerPopComponent'
import DealerPeakControl from '@/components/generic/DealerPeakControl'

import { createNamespacedHelpers } from 'vuex'
const mapUserGetters = createNamespacedHelpers('user').mapGetters
const mapDealerState = createNamespacedHelpers('dealer').mapState

export default {
  name: 'CustomerDirectoryComponent',
  mixins: [utilitiesMixin],
  components: {
    'dealer-selector': DealerSelector,
    'click-to-call': ClickToCallComponent,
    'section-header': SectionHeader,
    'customer-pop-component': CustomerPopComponent,
    'dealer-peak-control': DealerPeakControl
  },
  props: {
    //CustomerRollOut has always passed this, but the prop was never declared here, so switching to
    //the Customers tab left the caret wherever it was and the operator had to reach for the mouse
    //before typing. The company directory has had the same prop and watcher all along - this is the
    //parity Craig asked for on the call.
    inFocus: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isSearchingCustomer: false,
      selectedCustomer: null,
      selectedLastContact: null,
      customerSearchResults: [],
      searchCriteria: '',
      searchList: [],
      selectedDealer: this.currentDealer,
      customerSearchOpen: false,
      customerDetailsCollapsed: false,
      lastCustomerContactsOpen: true,
      matchedLastContacts: [],
      lastCustomerContacts: [],
      lastCustomerContactLimit: 100,
      lastSearchTerm: '',
    }
  },
  computed: {
    ...mapUserGetters(['currentUser']),
    ...mapDealerState(['currentDealer']),
    customerSearchQuery: function () {
      let query = ''
      if (this.searchCriteria !== '') query = this.searchCriteria
      return query.trim()
    }
  },
  methods: {
    setSearchFocus: function () {
      let that = this
      this.$nextTick(() => {
        if (that.$refs.searchInput && that.$refs.searchInput.focus) {
          that.$refs.searchInput.focus()
        }
      })
    },
    onShowLastContacted: function (value) {
      if (!value) this.lastCustomerContactsOpen = true
    },
    onShowCustomerSearch: function (value) {
      if (!value) this.customerSearchOpen = true
    },
    isPhoneNumber(value) {
      const phoneRegex = /(?<!\w)\+?\d{0,3}\s*\(\d{2,4}\)|(?<!\w)\+?\d{0,3}\s*\d{2,4}/
      return phoneRegex.test(value)
    },
    searchCustomer: async function () {
      if (this.customerSearchQuery === '' || this.isSearchingCustomer) {
        this.lastCustomerContactsOpen = true
        return
      }

      const matches = this.matchLastContacts(this.customerSearchQuery)
      this.matchedLastContacts = matches

      this.selectedCustomer = null
      this.selectedLastContact = null

      // Strip matches are a shortcut, not a replacement for the search. Returning here
      // left every customer outside the last 100 tracked calls unreachable, because only
      // doCustomerSearch() queries the database.
      this.lastCustomerContactsOpen = matches.length > 0
      this.doCustomerSearch()
    },
    doCustomerSearch: _.debounce(async function () { this.executeCustomerSearch() }, 500),
    executeCustomerSearch: async function () {
      if (this.lastSearchTerm === this.customerSearchQuery) {
        return
      }

      this.customerSearchResults = []
      this.selectedLastContact = null
      // Keep the strip open when it has its own matches - results and strip now coexist.
      this.lastCustomerContactsOpen = (this.matchedLastContacts || []).length > 0
      this.customerSearchOpen = true
      this.isSearchingCustomer = true
      
      let criteria = {
        page: 0,
        pageSize: 250,
        dealerId: this.currentDealer.id,
        query: this.searchCriteria,
        sortBy: 'firstName.asc'
      }

      customerService.getCustomerDirectoryList(criteria).then(response => {
        let data = response.results
        let list = data.map(function (x) {
          return {
            rowType: 'data',
            ...x
          }
        })
        this.customerSearchResults = [].concat(...list)
        this.isSearchingCustomer = false
        this.lastSearchTerm = this.customerSearchQuery
      })
    },
    getAsyncMatchList: _.debounce(async function (searchCriteria) {
      if (!searchCriteria || !searchCriteria.length) {
        this.lastSearchTerm = ''
        this.searchList = []
        return
      }
      
      if (this.isPhoneNumber(searchCriteria)) {
        let unmaskedNumber = this.unmask(searchCriteria)
        if (unmaskedNumber.length > 2) {
          let filterResults = await customerService.mobileList(searchCriteria.trim())
          this.searchList = filterResults.data
          if (unmaskedNumber.length === 10) {
            this.searchCustomer()
          }
        } else {
          this.searchList = []
        }
      } else if (searchCriteria.includes('@')) {
        let filterResults = await customerService.emailList(searchCriteria.trim())
        this.searchList = filterResults.data
        let validEmailAddress = searchCriteria.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/g)
        if (validEmailAddress) {
          this.searchCustomer()
        }
      } else {
        this.searchList = []
      }

      this.searchCustomer()
    }),
    loadContacts: async function () {
      if (this.lastCustomerContacts.length > 0) return

      this.isSearchingCustomer = true
      try {
        customerService.findLastCustomerContactByUser(this.currentUser.id, this.lastCustomerContactLimit).then(response => {
          let data = response.results
          let list = data.map(function (x) {
            return {
              rowType: 'data',
              ...x
            }
          })
          this.lastCustomerContacts = [].concat(...list)
          this.isSearchingCustomer = false
        })
      }
      catch (error) {
        console.log('Error: ', error)
        this.failedToast('An error occured while trying to load your last 20 contacts')
      }
      finally {
        this.isSearchingCustomer = false
      }
    },
    onRowSelected: function (row) {
      // Cleared so the Last Contacted heading cannot keep showing a stale name
      // once the selection has moved to a search result.
      this.selectedLastContact = null
      this.selectedCustomer = row
    },
    onLastContactSelected: function (customer) {
      this.selectedLastContact = customer
      this.selectedCustomer = customer
    },
    onCustomerDetailsCollapsedChanged: function (isCollapsed) {
      this.customerDetailsCollapsed = isCollapsed
    },
    customerExtraDisplayOption: function (item) {
      if (!item) {
        return ''
      }
      if (item.mobile && item.mobile !== '') {
        return item.mobile
      }
      if (item.telephone && item.telephone !== '') {
        return item.telephone
      }
      return ''
    },
    onCopy: function (type, data) {
      let that = this
      this.$copyText(data).then(function () {
        that.$buefy.toast.open(
          {
            duration: 2500,
            message: type + ' copied to clipboard',
            position: 'is-top',
            type: 'is-info'
          })
      }, function () {
        that.$buefy.toast.open(
          {
            duration: 2500,
            message: 'Could not copy ' + type + ' to clipboard',
            position: 'is-top',
            type: 'is-warning'
          })
      })
    },
    matchLastContacts(searchTerm) {
      if (!searchTerm) return []

      const term = searchTerm.toLowerCase()
      // Mobiles are stored masked as +1(561)-234-5678, so a digits-only query never
      // substring-matches the stored value. Unmask both sides before comparing, and skip
      // the comparison when the term carries no digits at all - unmask('+1') is '', and
      // ''.includes('') is true, which would match every contact in the strip.
      const digits = this.unmask(searchTerm)

      return this.lastCustomerContacts.filter(c =>
        (c.fullName && c.fullName.toLowerCase().includes(term)) ||
        (c.mobile && digits !== '' && this.unmask(c.mobile).includes(digits)) ||
        (c.email && c.email.toLowerCase().includes(term))
      )
    },
    openCustomerRecord () {
      this.$router.push({name: 'CustomersQuickView', params: { id: this.selectedCustomer.id }})
    }
  },
  watch: {
    inFocus: function (newValue) {
      if (newValue === true) {
        this.setSearchFocus()
      }
    },
    selectedCustomer: function (newValue, oldValue) {
      if (newValue) {
        this.customerSearchOpen = false
        this.lastCustomerContactsOpen = false
        // A manual collapse leaves the header's own isCollapsed true while the
        // prop sits at false, so re-asserting false would be a no-op. Tracking
        // it here means this flips true -> false and the watcher fires.
        this.customerDetailsCollapsed = false
        // Customer Details renders above the other panels now, so a selection
        // made further down would otherwise leave it off-screen.
        this.$nextTick(() => {
          let container = this.$el.querySelector('.view-container')
          if (container && container.scrollIntoView) {
            container.scrollIntoView({behavior: 'smooth', block: 'start'})
          }
        })
      }
      // Deliberately no `newValue !== oldValue` branch. On a selection it only repeated the
      // two assignments above, and on a DE-selection (null) it fired anyway - so clearing
      // the search box collapsed the strip that the searchCriteria watcher had just opened,
      // one tick earlier.
    },
    selectedLastContact: function (newValue) {
      if (newValue) {
        this.customerSearchOpen = false
        this.lastCustomerContactsOpen = false
      }
    },
    lastCustomerContactsOpen: async function (newValue) {
      if (newValue) {
        await this.loadContacts()
      }
    },
    searchCriteria: function (newValue, oldValue) {
      if (newValue === '') {
        this.matchedLastContacts = []
        this.selectedCustomer = null
        this.selectedLastContact = null
        this.customerSearchResults = []
        this.lastSearchTerm = ''
        this.lastCustomerContactsOpen = true
        this.customerSearchOpen = false
        return
      }

      if (newValue !== oldValue) {
        this.matchedLastContacts = []
        this.selectedCustomer = null
        this.selectedLastContact = null
        this.customerSearchResults = []
        this.lastSearchTerm = ''
      }
    }
  },
  mounted: async function () {
    if (this.inFocus) {
      this.setSearchFocus()
    }
    await this.loadContacts()
  },
  beforeDestroy () {
    axios.cancel('customer-directory-list')
    axios.cancel('customer-last-contact-search')
    axios.cancel('customer-mobile-list-search')
    axios.cancel('customer-email-list-search')
  }
}

</script>

<style scoped>
  .fab-wrapper {
    z-index: 999;
  }

  .fab-main {
    border-radius: 100px;
    width: 55px;
    height: 55px;
    padding: 30px;
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    box-shadow: 0 10px 10px rgba(0, 0, 0, 0.20), 0 4px 4px rgba(0, 0, 0, 0.15);
    z-index: 2;
    justify-content: center;
  }

  .fab-main .fal {
    color: white;
    -webkit-transition: .4s all;
    -moz-transition: .4s all;
    transition: .4s all;
    margin: 0px auto;
  }

  .fab-main .fal.main {
    opacity: 1;
    position: absolute;
  }

  .fab-main .fal.close {
    opacity: 0;
    position: absolute;
  }

  .fab-main .fal.main.rotate {
    -ms-transform: rotate(315deg); /* IE 9 */
    -webkit-transform: rotate(315deg); /* Chrome, Safari, Opera */
    transform: rotate(315deg);
    opacity: 0;
    -webkit-transition: opacity .3s ease-in, -webkit-transform .4s; /* Safari */
    transition: opacity .3s ease-in, transform .4s;
  }

  .fab-main .fal.close.rotate {
    -ms-transform: rotate(315deg); /* IE 9 */
    -webkit-transform: rotate(315deg); /* Chrome, Safari, Opera */
    transform: rotate(315deg);
    opacity: 1;
    -webkit-transition: opacity .3s ease-in, -webkit-transform .4s; /* Safari */
    transition: opacity .3s ease-in, transform .4s;
  }

  .pointer {
    cursor: pointer;
  }

  .customer-search-body {
    height: calc(100vh - 350px);
    padding-top:25px;
  }

  .call-log, .call-log .view-container, .call-log .card-content {
    overflow: visible !important;
    max-height: none !important;
  }

  .call-log .view-container.box {
    height: auto !important;
  }

  .card-content {
    padding: 0;
  }

  /* Spacing matched to CompanyDirectoryComponent so the two directories read
     as one surface. The selector-label rule from that file is deliberately
     absent - this component does not use the button list selector. */
  .call-log .box {
    padding: 0.75rem;
  }

  .call-log .columns:not(:last-child) {
    margin-bottom: 1rem;
  }

  /* Same spacing as the company directory's buttons, so the two tabs match - the Last Contacted
     buttons ran together with no gap between them. .modal-card is there for specificity, as it is
     there: main.scss (which imports Bulma) loads after component styles and wins a tie. */
  .call-log.modal-card ::v-deep .boption.button {
    font-size: 0.85rem;
    padding-left: 0.7em;
    padding-right: 0.7em;
    margin-right: 0.35rem;
    margin-bottom: 0.35rem;
    border-radius: 3px;
  }

  /* card-header-title is display:flex, so the selected-name span would sit
     inline with the subtitle and get squeezed once the number is appended.
     Stacking keeps the full value readable at any length. */
  .call-log .card-header-title.stacked-heading {
    flex-direction: column;
    align-items: flex-start;
  }
</style>